import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";

/**
 * The `beforeinstallprompt` event (Chromium-only) lets us defer the native
 * install prompt and trigger it from our own UI (e.g. an "Install" button).
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

// `beforeinstallprompt` fires only once per page load, but the Navbar (and this
// hook) remounts on every client-side route change. Keep the captured prompt in
// module scope so a remount can recover it instead of losing the Install button.
let savedInstallPrompt: BeforeInstallPromptEvent | null = null;
let appWasInstalled = false;

/**
 * True on iOS Safari (iPhone/iPad/iPod) including iPadOS, which reports a desktop
 * "Macintosh" UA but exposes touch points. iOS has no `beforeinstallprompt`, so we
 * use this to offer a manual "Add to Home Screen" flow instead.
 */
function detectIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const isAppleMobile = /iPad|iPhone|iPod/.test(ua);
  const isIPadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  return isAppleMobile || isIPadOS;
}

/** Update the viewport meta so pinch/zoom can be toggled at runtime. */
function setViewportScalable(scalable: boolean) {
  if (typeof document === "undefined") return;
  const meta = document.querySelector(
    'meta[name="viewport"]',
  ) as HTMLMetaElement | null;
  if (!meta) return;
  meta.setAttribute(
    "content",
    scalable
      ? "width=device-width, initial-scale=1, viewport-fit=cover"
      : "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover",
  );
}

/**
 * PWA-awareness hook for the customer web app.
 *
 * Exposes:
 * - `isStandalone` — running as an installed PWA (display-mode standalone / iOS).
 * - `isIOS` — iOS Safari, which has no install prompt (use a manual A2HS dialog).
 * - `canInstall` + `promptInstall()` — the deferred Chromium install prompt.
 * - `canGoBack` — there is in-app navigation history to pop (for a back button
 *   when running standalone, where there is no browser chrome).
 *
 * Side effect: while standalone, pinch-zoom is disabled via the viewport meta
 * (restored when not standalone) so the installed app feels native.
 */
export default function usePwa() {
  const router = useRouter();
  const [isStandalone, setIsStandalone] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [canInstall, setCanInstall] = useState(false);
  const [canGoBack, setCanGoBack] = useState(false);
  // In-app navigation depth; a push increments, a back (popstate) decrements.
  const navDepth = useRef(0);
  const popping = useRef(false);

  // Detect standalone display mode and keep it in sync. Restricts zoom while
  // installed so pinch-gestures don't break the app-like feel.
  useEffect(() => {
    if (typeof window === "undefined") return;
    setIsIOS(detectIOS());
    const mql = window.matchMedia("(display-mode: standalone)");
    const compute = () => {
      const standalone =
        mql.matches ||
        // iOS Safari exposes standalone on navigator instead of display-mode.
        (window.navigator as unknown as { standalone?: boolean }).standalone ===
          true;
      setIsStandalone(standalone);
      setViewportScalable(!standalone);
    };
    compute();
    mql.addEventListener?.("change", compute);
    return () => {
      mql.removeEventListener?.("change", compute);
      // Restore zoom on unmount so a non-standalone view stays accessible.
      setViewportScalable(true);
    };
  }, []);

  // Capture the deferred install prompt; clear it once installed. The prompt is
  // stored in module scope so it survives the Navbar remounting on navigation —
  // a fresh mount re-reads it (the browser won't re-fire beforeinstallprompt).
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (savedInstallPrompt && !appWasInstalled) setCanInstall(true);

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      savedInstallPrompt = e as BeforeInstallPromptEvent;
      setCanInstall(true);
    };
    const onInstalled = () => {
      savedInstallPrompt = null;
      appWasInstalled = true;
      setCanInstall(false);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Track in-app navigation depth so a back button only shows when there is
  // somewhere to go back to.
  useEffect(() => {
    const onComplete = () => {
      if (popping.current) {
        navDepth.current = Math.max(0, navDepth.current - 1);
        popping.current = false;
      } else {
        navDepth.current += 1;
      }
      setCanGoBack(navDepth.current > 0);
    };
    router.beforePopState(() => {
      popping.current = true;
      return true;
    });
    router.events.on("routeChangeComplete", onComplete);
    return () => {
      router.events.off("routeChangeComplete", onComplete);
    };
  }, [router]);

  const promptInstall = useCallback(async () => {
    const prompt = savedInstallPrompt;
    if (!prompt) return;
    await prompt.prompt();
    await prompt.userChoice;
    savedInstallPrompt = null;
    setCanInstall(false);
  }, []);

  const goBack = useCallback(() => {
    router.back();
  }, [router]);

  return { isStandalone, isIOS, canInstall, promptInstall, canGoBack, goBack };
}
